/**
 * cinepak.js — распаковка видео Cinepak (FourCC «cvid») в кадры RGB. [12.0.45]
 *
 * ПОВОД. Владелец прислал 17 эхо-клипов из своего архива (один аппарат с ЭКГ
 * на экране, Philips 2010 года). Все они в AVI с кодеком Cinepak — старым
 * кодеком 1990-х, которым раньше писали видео для компьютеров. Браузер его не
 * распаковывает, и 12.0.41–12.0.44 честно отказывали: «сжато кодеком cvid».
 *
 * КАК УСТРОЕН CINEPAK. Кадр режется на полосы (strip) во всю ширину, полоса —
 * на блоки 4×4. Для блока хранятся номера из «книг» (codebook) по 256
 * записей; запись — четыре пикселя 2×2, яркость Y на каждый и общий цвет
 * (U, V). Блок либо растягивает одну запись книги V1 (каждый её пиксель
 * становится квадратом 2×2), либо собран из четырёх записей книги V4. Книги
 * живут между кадрами и обновляются частично; в межкадровом кадре блок можно
 * пропустить — тогда на экране остаётся прошлый кадр. Поэтому декодер хранит
 * состояние и кадры подаются строго по порядку.
 *
 * Цвет — тем же правилом, что в FFmpeg (libavcodec/cinepak.c), чтобы кадры
 * совпали с эталоном байт в байт:
 *   R = Y + 2V,  G = Y − ⌊U/2⌋ − V (деление к нулю),  B = Y + 2U, с обрезкой 0…255.
 * Проверено так: все 17 клипов владельца распакованы и этим файлом, и FFmpeg
 * (rgb24), и сравнены по каждому байту каждого кадра — см. CHANGELOG_12.0.45.
 * В тест (tests/avi_test.mjs) клипы не положены — в них впечатаны номера
 * исследований; там кадры собираются кодировщиком прямо в тесте.
 *
 * Файл без импортов.
 */

const clip = (v) => (v < 0 ? 0 : (v > 255 ? 255 : v));

/** Книга: 256 записей × 4 пикселя × RGB. */
const новаяКнига = () => new Uint8Array(256 * 12);

/**
 * Обновить книгу из куска. id: бит 0x04 — записи без цвета (4 байта Y),
 * иначе 6 байт (4 Y + U + V со знаком); бит 0x01 — частичное обновление: перед
 * каждыми 32 записями 32-битная маска, какие из них заменить.
 */
function читатьКнигу(книга, id, b, p, конец) {
  const n = (id & 0x04) ? 4 : 6;
  let флаг = 0, маска = 0;
  for (let i = 0; i < 256; i++) {
    if (id & 0x01) {
      маска >>>= 1;
      if (!маска) {
        if (p + 4 > конец) return;
        флаг = ((b[p] << 24) | (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) >>> 0;
        p += 4;
        маска = 0x80000000;
      }
    }
    if (!(id & 0x01) || (флаг & маска)) {
      if (p + n > конец) return;
      const о = i * 12;
      if (n === 4) {
        for (let k = 0; k < 4; k++) { const y = b[p++]; книга[о + k * 3] = y; книга[о + k * 3 + 1] = y; книга[о + k * 3 + 2] = y; }
      } else {
        const y0 = b[p], y1 = b[p + 1], y2 = b[p + 2], y3 = b[p + 3];
        const u = (b[p + 4] << 24) >> 24, v = (b[p + 5] << 24) >> 24;   // со знаком
        p += 6;
        const dr = v * 2, dg = -Math.trunc(u / 2) - v, db = u * 2;
        const ys = [y0, y1, y2, y3];
        for (let k = 0; k < 4; k++) {
          книга[о + k * 3] = clip(ys[k] + dr);
          книга[о + k * 3 + 1] = clip(ys[k] + dg);
          книга[о + k * 3 + 2] = clip(ys[k] + db);
        }
      }
    }
  }
}

/** Блоки полосы: V1 (одна запись на 4×4) или V4 (четыре записи), пропуск в межкадровом. */
function читатьБлоки(к, полоса, id, b, p, конец) {
  const { ширина: W, высота: H, rgb } = к;
  let флаг = 0, маска = 0;
  const взятьФлаг = () => {
    if (p + 4 > конец) throw new Error('Cinepak: кусок блоков оборван');
    флаг = ((b[p] << 24) | (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) >>> 0;
    p += 4;
    маска = 0x80000000;
  };
  // Строки блока: y, y+1, y+2, y+3; у нижнего края лишние строки пишутся в последнюю существующую.
  const строка = (y) => Math.min(y, H - 1) * W * 3;
  const пиксель = (row, x, src, so) => {
    if (x >= W) return;
    const d = row + x * 3;
    rgb[d] = src[so]; rgb[d + 1] = src[so + 1]; rgb[d + 2] = src[so + 2];
  };
  for (let y = полоса.y1; y < полоса.y2; y += 4) {
    const r0 = строка(y), r1 = строка(y + 1), r2 = строка(y + 2), r3 = строка(y + 3);
    for (let x = полоса.x1; x < полоса.x2; x += 4) {
      if (id & 0x01) { маска >>>= 1; if (!маска) взятьФлаг(); }
      if ((id & 0x01) && !(флаг & маска)) continue;          // блок пропущен — остаётся прошлый кадр
      if (!(id & 0x02)) { маска >>>= 1; if (!маска) взятьФлаг(); }
      if ((id & 0x02) || (~флаг & маска)) {
        // V1: запись из четырёх пикселей, каждый — квадрат 2×2.
        if (p >= конец) throw new Error('Cinepak: кусок блоков оборван');
        const кн = полоса.v1, о = b[p++] * 12;
        // Порядок строк — снизу вверх, как в FFmpeg: у нижнего края лишние
        // строки падают на последнюю существующую, и побеждает верхняя.
        for (const [row, k] of [[r3, 2], [r2, 2], [r1, 0], [r0, 0]]) {
          пиксель(row, x, кн, о + k * 3); пиксель(row, x + 1, кн, о + k * 3);
          пиксель(row, x + 2, кн, о + (k + 1) * 3); пиксель(row, x + 3, кн, о + (k + 1) * 3);
        }
      } else {
        // V4: четыре записи, каждая — свой квадрат 2×2.
        if (p + 4 > конец) throw new Error('Cinepak: кусок блоков оборван');
        const кн = полоса.v4;
        const c0 = b[p] * 12, c1 = b[p + 1] * 12, c2 = b[p + 2] * 12, c3 = b[p + 3] * 12;
        p += 4;
        const ряд = (row, cl, cr, верх) => {
          const s = верх ? 0 : 6;
          пиксель(row, x, кн, cl + s); пиксель(row, x + 1, кн, cl + s + 3);
          пиксель(row, x + 2, кн, cr + s); пиксель(row, x + 3, кн, cr + s + 3);
        };
        ряд(r3, c2, c3, false); ряд(r2, c2, c3, true); ряд(r1, c0, c1, false); ряд(r0, c0, c1, true);
      }
    }
  }
}

/**
 * Декодер одного видеопотока: кадры подаются по порядку.
 * @param {number} ширина
 * @param {number} высота
 */
export function декодерCinepak(ширина, высота) {
  if (!(ширина > 0 && высота > 0 && ширина <= 4096 && высота <= 4096)) throw new Error('Cinepak: размер кадра вне разумного');
  const к = { ширина, высота, rgb: new Uint8Array(ширина * высота * 3), полосы: [] };
  /**
   * @param {Uint8Array} b файл
   * @param {number} off начало кадра
   * @param {number} len длина кадра
   * @returns {Uint8Array} новый массив RGB (копия, ширина·высота·3)
   */
  function кадр(b, off, len) {
    const конец = off + len;
    if (len < 10) throw new Error('Cinepak: кадр короче заголовка');
    const флагиКадра = b[off];
    const полос = (b[off + 8] << 8) | b[off + 9];
    let p = off + 10;
    let y0 = 0;
    for (let i = 0; i < Math.min(полос, 32); i++) {
      if (p + 12 > конец) throw new Error('Cinepak: заголовок полосы оборван');
      if (!к.полосы[i]) к.полосы[i] = { v1: новаяКнига(), v4: новаяКнига() };
      const пол = к.полосы[i];
      const размер = ((b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) - 12;
      let y1 = (b[p + 4] << 8) | b[p + 5];
      const x1 = (b[p + 6] << 8) | b[p + 7];
      let y2 = (b[p + 8] << 8) | b[p + 9];
      const x2 = (b[p + 10] << 8) | b[p + 11];
      if (!y1) { y1 = y0; y2 = y0 + y2; }                    // «относительно прошлой полосы»
      if (размер < 0) throw new Error('Cinepak: отрицательная длина полосы');
      p += 12;
      const конецПолосы = Math.min(p + размер, конец);
      // Без бита 0x01 в флагах кадра каждая полоса начинает с книг предыдущей.
      if (i > 0 && !(флагиКадра & 0x01)) { пол.v1.set(к.полосы[i - 1].v1); пол.v4.set(к.полосы[i - 1].v4); }
      Object.assign(пол, { y1, y2, x1, x2: Math.min(x2, ширина) });
      let q = p, блоки = false;
      while (q + 4 <= конецПолосы) {
        const id = b[q];
        let размерКуска = ((b[q + 1] << 16) | (b[q + 2] << 8) | b[q + 3]) - 4;
        if (размерКуска < 0) throw new Error('Cinepak: отрицательная длина куска');
        q += 4;
        if (q + размерКуска > конецПолосы) размерКуска = конецПолосы - q;
        if (id === 0x20 || id === 0x21 || id === 0x24 || id === 0x25) читатьКнигу(пол.v4, id, b, q, q + размерКуска);
        else if (id === 0x22 || id === 0x23 || id === 0x26 || id === 0x27) читатьКнигу(пол.v1, id, b, q, q + размерКуска);
        else if (id === 0x30 || id === 0x31 || id === 0x32) { читатьБлоки(к, пол, id, b, q, q + размерКуска); блоки = true; break; }
        q += размерКуска;
      }
      if (!блоки) throw new Error(`Cinepak: в полосе ${i + 1} нет блоков`);
      p = конецПолосы;
      y0 = y2;
    }
    return к.rgb.slice();
  }
  return { кадр };
}

export default { декодерCinepak };
